import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, TrendingDown, Download, BarChart2, Package, Printer } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import AdminLayout from '../../../layouts/AdminLayout';
import { reportsApi } from '../../../services/api';
import { formatCurrency } from '../../../utils/helpers';
import { useLanguage } from '../../../contexts/LanguageContext';

function DateRange({ start, end, onStart, onEnd }) {
  return (
    <div className="flex items-center gap-2">
      <input type="date" value={start} onChange={e => onStart(e.target.value)} data-testid="profit-loss-date-start"
        className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44]" />
      <span className="text-slate-400 text-sm">—</span>
      <input type="date" value={end} onChange={e => onEnd(e.target.value)} data-testid="profit-loss-date-end"
        className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#0f6c44]" />
    </div>
  );
}

function StatCard({ label, value, sub, color = 'slate', icon: Icon }) {
  const colors = {
    green: 'bg-green-50 text-green-700 border-green-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
  };
  return (
    <div className={`rounded-2xl border p-5 ${colors[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
        </div>
        {Icon && <Icon size={22} className="opacity-40 mt-0.5" />}
      </div>
    </div>
  );
}

export default function ProfitLossPage() {
  const { t } = useLanguage();
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = today.slice(0, 8) + '01';
  const [start, setStart] = useState(firstDay);
  const [end, setEnd] = useState(today);

  const { data, isLoading } = useQuery({
    queryKey: ['reports-summary', start, end],
    queryFn: () => reportsApi.getSummary(start, end).then(r => r.data),
  });

  const { data: prodPerf, isLoading: loadProd } = useQuery({
    queryKey: ['product-perf', start, end],
    queryFn: () => reportsApi.getProductPerformance(start, end).then(r => r.data),
  });

  const handleExportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: html2canvas } = await import('html2canvas');
    const el = document.getElementById('profit-loss-report');
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`LaporanLabaRugi-${start}-${end}.pdf`);
  };

  const exportUrl = reportsApi.getExportUrl('excel', start, end);

  return (
    <AdminLayout>
      <div className="space-y-6" id="profit-loss-report">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Laporan Laba Rugi', 'Profit & Loss')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Ringkasan keuangan per periode', 'Financial summary by period')}</p>
          </div>
            <div className="flex items-center gap-2 flex-wrap">
            <DateRange start={start} end={end} onStart={setStart} onEnd={setEnd} />
            <button onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 bg-white text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors">
              <Printer size={15} />PDF
            </button>
            <a href={exportUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] transition-colors">
              <Download size={15} />{t('Export Excel', 'Export Excel')}
            </a>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label={t('Total Omset', 'Revenue')} value={formatCurrency(data?.omset || 0)} sub={`${data?.total_orders || 0} pesanan`} color="blue" icon={BarChart2} />
              <StatCard label={t('Kas Masuk', 'Cash In')} value={formatCurrency(data?.kas_masuk || 0)} sub={t('Sudah terbayar', 'Paid')} color="green" icon={TrendingUp} />
              <StatCard label={t('Total Biaya', 'Total Costs')} value={formatCurrency(data?.total_costs || 0)} color="red" icon={TrendingDown} />
              <StatCard label={t('Laba Bersih', 'Net Profit')} value={formatCurrency(data?.laba_bersih || 0)}
                color={(data?.laba_bersih || 0) >= 0 ? 'green' : 'red'} icon={TrendingUp} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <StatCard label={t('Piutang', 'Receivables')} value={formatCurrency(data?.piutang || 0)} sub={t('Belum terbayar', 'Unpaid')} color="orange" />
              <StatCard label={t('Pesanan Lunas', 'Paid Orders')} value={`${data?.paid_orders || 0} / ${data?.total_orders || 0}`} sub={t('pesanan lunas', 'paid orders')} color="slate" />
            </div>
          </>
        )}

        <div data-testid="profit-loss-daily-chart" className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="mb-5"><h2 className="font-manrope font-semibold text-slate-800">Omset vs Biaya Operasional Harian</h2><p className="mt-0.5 text-xs text-slate-400">Bandingkan pendapatan pesanan dan biaya operasional per tanggal.</p></div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.chart_data || []} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs><linearGradient id="omset-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0f6c44" stopOpacity={0.32}/><stop offset="95%" stopColor="#0f6c44" stopOpacity={0}/></linearGradient><linearGradient id="biaya-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.24}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis tickFormatter={value => `${Math.round(value / 1000)}k`} tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} width={42} />
                <Tooltip formatter={(value, name) => [formatCurrency(value), name === 'omset' ? 'Omset' : 'Biaya Operasional']} labelFormatter={label => `Tanggal ${label}`} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Legend formatter={value => value === 'omset' ? 'Omset' : 'Biaya Operasional'} />
                <Area type="monotone" dataKey="omset" stroke="#0f6c44" strokeWidth={2.5} fill="url(#omset-fill)" activeDot={{ r: 5 }} />
                <Area type="monotone" dataKey="costs" stroke="#ef4444" strokeWidth={2.5} fill="url(#biaya-fill)" activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performa Produk */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-50">
            <Package size={17} className="text-[#0f6c44]" />
            <h2 className="font-semibold text-slate-800">{t('Rincian Performa Produk', 'Product Performance')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-50">
                  {[t('Layanan / Produk', 'Service / Product'), t('Kategori', 'Category'), t('Volume Qty', 'Qty'), t('Total Penjualan', 'Revenue'), 'Total HPP', t('Laba Kotor', 'Gross Profit'), 'Margin'].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadProd ? [1,2,3].map(i => (
                  <tr key={i}>{[1,2,3,4,5,6,7].map(j => <td key={j} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>
                )) : (prodPerf?.length === 0) ? (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400 text-sm">{t('Belum ada data', 'No data')}</td></tr>
                ) : (prodPerf || []).map((p, i) => {
                  const margin = p.total_penjualan > 0 ? ((p.laba_kotor / p.total_penjualan) * 100).toFixed(1) : '0.0';
                  return (
                    <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                      <td className="px-5 py-3.5 text-sm font-medium text-slate-800">{p.product_name}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{p.category || '-'}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">{p.total_qty} {p.unit}</td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-slate-800">{formatCurrency(p.total_penjualan)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{formatCurrency(p.total_hpp)}</td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-green-700">{formatCurrency(p.laba_kotor)}</td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${parseFloat(margin) >= 30 ? 'bg-green-100 text-green-700' : parseFloat(margin) >= 10 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {margin}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
